import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useCallback, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { Toaster } from 'sonner';
import { queryClient } from '@lib/queryClient';
import 'driver.js/dist/driver.css';

import type { Session } from '@supabase/supabase-js';
import {
  DesktopAppDownloadDialog,
  hasSeenDesktopAppPrompt,
} from '@components/common/DesktopAppDownloadDialog';
import { FeatureReleaseModal } from '@components/common/FeatureReleaseModal';
import { InitialSyncOverlay } from '@components/common/InitialSyncOverlay';
import { OutletLiveSync } from '@components/common/OutletLiveSync';
import { SplashScreen } from '@components/common/SplashScreen';
import { SyncEngineFallbackBanner } from '@components/common/SyncEngineFallbackBanner';
import { SystemClockWarning } from '@components/common/SystemClockWarning';
import { UpdateBanner } from '@components/common/UpdateBanner';
import { useClockSkewDetection } from '@hooks/useClockSkewDetection';
import { useSessionExpiryGuard } from '@hooks/useSessionExpiryGuard';
import { selectIsInialized } from '@store/config/configSelectors';
import { initializeConfig } from '@store/config/configThunks';
import './App.css';
import { useAppDispatch, useAppSelector } from './hooks/useRedux';
import { clearExpiredSessionStorage } from './lib/auth/sessionStorage';
import { consumeUserInitiatedSignOut } from './lib/auth/signOutIntent';
import { ensureOutletSync } from './lib/powersync/ensureOutletSync';
import { syncEvents } from './lib/powersync/supabaseConnector';
import { AppRoutes } from './routes';
import { supabase } from './services/supabase';
import { store } from './store';
import { selectCurrentUser } from './store/auth/authSelectors';
import { resetAuthState } from './store/auth/authSlice';
import { fetchCurrentUser, logout } from './store/auth/authThunks';
import { isElectron } from './util';
import { apiError } from './util/notify';

// Relays the renderer's Supabase session to the Electron main process, where
// PowerSync now runs (see src/main/lib/powersync/). Main has no localStorage
// of its own, so it depends on this relay (plus the persisted copy it writes
// to disk) to stay authenticated. No-op on the web build.
function pushSessionToMain(event: string, session: Session | null): void {
  if (!isElectron()) return;
  const relayableEvents = new Set([
    'SIGNED_IN',
    'SIGNED_OUT',
    'TOKEN_REFRESHED',
    'USER_UPDATED',
    'INITIAL_SESSION',
  ]);
  if (!relayableEvents.has(event)) return;

  window.electron.auth.pushSession({
    event: event as
      | 'SIGNED_IN'
      | 'SIGNED_OUT'
      | 'TOKEN_REFRESHED'
      | 'USER_UPDATED'
      | 'INITIAL_SESSION',
    session: session
      ? {
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          expires_at: session.expires_at ?? null,
          user_id: session.user.id,
        }
      : null,
  });
}

// Single place a session ends — whether the user signed out, the server
// rejected the refresh token, or useSessionExpiryGuard found it dead. Tells
// main (so PowerSync stops asking for credentials) and resets auth state with
// isInitialized kept true, which lets routes/index.tsx redirect to the login
// screen. Local PowerSync data is never touched, so queued sales survive.
function handleSessionEnded(
  dispatch: ReturnType<typeof useAppDispatch>,
  userInitiated: boolean,
): void {
  const expiredUser = store.getState().auth.user;
  const hadUser = Boolean(expiredUser);
  if (!userInitiated) {
    // Expired / rejected session: drop its auth state from localStorage so
    // nothing (e.g. getCurrentUser()'s cached-profile fallback) can restore
    // this user without a real session. A deliberate sign-out keeps the
    // cache so offline sign-in still works for shift hand-offs.
    clearExpiredSessionStorage(localStorage, expiredUser?.id);
  }
  pushSessionToMain('SIGNED_OUT', null);
  dispatch(resetAuthState());
  if (hadUser && !userInitiated) {
    apiError('Your session has expired — please sign in again.');
  }
}

function AppContent() {
  const [isLoading, setIsLoading] = useState(true);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const isInitialized = useAppSelector(selectIsInialized);
  const currentUser = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();

  // Modal priority chain (see render section below): SystemClockWarning is
  // the only one of these three that self-gates internally, so its skew
  // state is re-derived here to suppress the lower-priority modals.
  const isClockSkewed = useClockSkewDetection();
  const isDesktopPromptEligible =
    !isElectron() && !!currentUser && !hasSeenDesktopAppPrompt();

  // Initialize config data when app loads
  useEffect(() => {
    const loadConfig = async () => {
      await dispatch(initializeConfig());
      setIsConfigLoaded(true);
    };
    loadConfig();
  }, [dispatch]);

  // Auth state listener - reacts to auth changes (login/logout)
  useEffect(() => {
    // Check for existing session on app start.
    // detectSessionInUrl is disabled to avoid race conditions, so we handle
    // both OAuth callback formats explicitly here:
    //   PKCE flow   → ?code= in query params → exchangeCodeForSession()
    //   Implicit flow → #access_token= in fragment → setSession()
    const checkSession = async () => {
      if (typeof window !== 'undefined') {
        // PKCE flow: Supabase redirects back with ?code=...
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (code) {
          await supabase.auth.exchangeCodeForSession(window.location.href);
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
        } else {
          // Implicit flow: Supabase returns tokens in the URL hash fragment
          const hash = window.location.hash.slice(1);
          const hashParams = new URLSearchParams(hash);
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          if (accessToken && refreshToken) {
            await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname,
            );
          }
        }
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      // Push the current session to main eagerly on boot, so the
      // main-process PowerSync connector can authenticate as soon as
      // possible rather than waiting for the next auth event.
      pushSessionToMain('INITIAL_SESSION', session);
      if (session?.user) {
        dispatch(fetchCurrentUser());
      }
    };
    checkSession();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event as string) === 'TOKEN_REFRESH_FAILED') {
        // Token refresh failed — likely a network issue or clock skew.
        // SystemClockWarning will detect and surface a skewed clock; if the
        // clock is fine this is a legitimate session expiry.
        handleSessionEnded(dispatch, false);
        return;
      }
      if (event === 'SIGNED_OUT' || !session) {
        // Signed out deliberately, or supabase-js discarded a session the
        // server rejected (e.g. invalid refresh token).
        handleSessionEnded(dispatch, consumeUserInitiatedSignOut());
        return;
      }
      pushSessionToMain(event, session);
      if (event === 'SIGNED_IN' && session?.user) {
        // User signed in - fetch user profile
        dispatch(fetchCurrentUser());
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Token was refreshed, update user if needed
        dispatch(fetchCurrentUser());
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch]);

  // Never let a signed-in user sit on a dead session (see the hook).
  const handleSessionExpired = useCallback(() => {
    // signOut clears the stored session and normally fires SIGNED_OUT (handled
    // above); call the handler directly too in case there was no session to
    // sign out of (a user restored offline from the cached profile).
    supabase.auth
      .signOut({ scope: 'local' })
      .catch(() => {
        /* best-effort — local state is reset below regardless */
      })
      .finally(() => handleSessionEnded(dispatch, false))
      .catch(() => {
        /* handleSessionEnded never throws; keeps the linter happy */
      });
  }, [dispatch]);
  useSessionExpiryGuard(handleSessionExpired);

  // Main briefly has no active outlet while activation is in flight — normal
  // and already tolerated. But if local data reads keep failing with "No
  // outlet is active yet" well past that window (see ipcBridge.ts's
  // watchdog), the native main-process sync engine never produced a usable
  // local database. On Electron this means the native engine failed — the
  // session is almost always fine, so we do NOT force a sign-out (that was the
  // misleading "please sign in again" toast). Instead we raise the same
  // `sync-engine-failed` signal the main process emits, which switches the app
  // to the in-renderer @powersync/web fallback and shows a clear banner.
  // On the web build there is no main engine, so keep the original sign-out.
  useEffect(() => {
    const handleOutletUnavailable = () => {
      if (isElectron()) {
        syncEvents.dispatchEvent(
          new CustomEvent('sync-engine-failed', {
            detail: {
              message:
                'Local data is unavailable — switching to compatibility sync.',
              stage: 'outlet-unavailable',
            },
          }),
        );
        return;
      }
      dispatch(logout());
      apiError('Your session needs to be refreshed — please sign in again.');
    };
    syncEvents.addEventListener('outlet-unavailable', handleOutletUnavailable);
    return () =>
      syncEvents.removeEventListener(
        'outlet-unavailable',
        handleOutletUnavailable,
      );
  }, [dispatch]);

  // "Resync Now" in the native Help menu (see main/menu.ts) — a last-resort
  // escape hatch so a user is never stuck just because PowerSync isn't
  // syncing, regardless of which page they're on. No-op on the web build
  // (no window.electron there).
  useEffect(() => {
    if (!isElectron()) return undefined;
    const handleRequestResync = () => {
      ensureOutletSync(currentUser?.outlet_id);
    };
    return window.electron.sync.onRequestResync(handleRequestResync);
  }, [currentUser?.outlet_id]);

  const handleAppReady = () => {
    setIsLoading(false);
  };

  return (
    <>
      {isLoading && (
        <SplashScreen
          onComplete={() => {
            if (isConfigLoaded) {
              setIsLoading(false);
            } else {
              // Wait for config to load, then hide
              const checkInterval = setInterval(() => {
                if (isInitialized) {
                  clearInterval(checkInterval);
                  setIsLoading(false);
                }
              }, 100);
            }
          }}
        />
      )}
      <div className={isLoading ? 'hidden' : 'block h-full'}>
        <AppRoutes onAppReady={handleAppReady} />
      </div>
      <InitialSyncOverlay />
      <OutletLiveSync />
      <SystemClockWarning />
      {/* Priority chain so root-level modals never visually collide:
          SystemClockWarning > DesktopAppDownloadDialog > FeatureReleaseModal. */}
      <DesktopAppDownloadDialog suppressed={isClockSkewed} />
      <FeatureReleaseModal
        suppressed={isClockSkewed || isDesktopPromptEligible}
      />
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <UpdateBanner />
        <SyncEngineFallbackBanner />
        <AppContent />
        <Toaster position="top-right" richColors closeButton />
      </Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
